import { useMemo, useState } from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
  Link,
  Button,
  Autocomplete,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { ClearIcon } from '@mui/x-date-pickers'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import { Visibility } from '@mui/icons-material'

import styles from './styles.module.scss'

import { buildTimeStringFromTimestamp } from '../../components/common/time/timeHelper'
import useDebounce from '../../components/hooks/useDebounce'
import { countryDictionary } from '../../components/common/flag/countries'
import useUserTableConfig from '../../components/hooks/useUserTableConfig'
import {
  getOrderedColumns,
  getVisibilityModel,
  updateListOrder,
} from '../../components/common/datatable/tableUtils/tableUtils'
import CustomColumnMenu from '../../components/common/datagrid/CustomColumnMenu'
import CustomLoadingOverlay from '../../components/common/datagrid/CustomLoadingOverlay'
import { setCurrentNationality, useFindKymPersonsQuery } from '../../features/kym/kymPersonsSlice'

const KymPersons = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['messages', 'common'])
  const queryState = useSelector((state) => state.kymPersons.filters)
  const [page, setPage] = useState(queryState.page)
  const nationality = queryState.nationality
  const [searchText, setSearchText] = useState()
  const searchTextDebounced = useDebounce(searchText, 500)

  const nationalityOptions = useMemo(() => {
    return Object.keys(countryDictionary).map((nationality) => ({
      value: nationality,
      label: countryDictionary[nationality],
    }))
  }, [])

  const columns = useMemo(() => [
    {
      field: 'externalId',
      headerName: t('common:externalId'),
      width: 150,
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '13px' }} variant="body2">
            {row.externalId || '---'}
          </Typography>
        )
      },
    },
    {
      field: 'name',
      headerName: t('common:firstName'),
      width: 150,
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '13px' }} variant="body2">
            {row.identity.name || '---'}
          </Typography>
        )
      },
    },
    {
      field: 'lastname',
      headerName: t('common:lastName'),
      width: 160,
      renderCell: ({ row }) => {
        return (
          <Typography sx={{ fontSize: '13px' }} variant="body2">
            {row.identity.lastname || '---'}
          </Typography>
        )
      },
    },
    {
      field: 'documents',
      headerName: t('common:documents'),
      width: 260,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box>
            {row.documents?.length ? (
              row.documents.map((doc, index) => (
                <Typography key={index} sx={{ fontSize: '13px' }}>
                  {t(`messages:kym.${doc.documentType}`)}: {doc.documentNumber}
                </Typography>
              ))
            ) : (
              <Typography sx={{ fontSize: '13px' }}>---</Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: t('common:createdAt'),
      width: 190,
      renderCell: ({ row }) => {
        return <Typography variant="number">{buildTimeStringFromTimestamp(row.createdAt)}</Typography>
      },
    },
    {
      field: 'kyb',
      headerName: t('messages:kym.column.filePerson'),
      width: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Link component={ReactRouterLink} title={row._id} to={`/kyc/${row.country}/detail/${row.kyb}`}>
          {row.kyb}
        </Link>
      ),
    },
    {
      field: 'kyt',
      headerName: 'KYT',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => {
        return row.kyt ? (
          <Link component={ReactRouterLink} title={row._id} to={`/kyt/users/consumer/${row.kyt}`}>
            {row.kyt}
          </Link>
        ) : (
          '---'
        )
      },
    },
    {
      field: 'kyc',
      headerName: t('common:Kyc'),
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.kycv2) {
          return (
            <Link component={ReactRouterLink} title={row._id} to={`/kycv2/person/${row.kycv2}`}>
              {row.kycv2}
            </Link>
          )
        }
        if (row.kyc) {
          return (
            <Link component={ReactRouterLink} title={row._id} to={`/kyc/person/${row.kyc}`}>
              {row.kyc}
            </Link>
          )
        }
        return '---'
      },
    },
  ])

  const {
    config,
    saveConfig,
    loading: configLoading,
  } = useUserTableConfig({
    tableKey: 'kym',
    defaultConfig: {
      sortModel: [{ field: 'createdAt', sort: 'desc' }],
      rowsPerPage: 10,
      pinnedColumns: { left: [], right: [] },
      columnOrder: columns.map((c) => c.field),
    },
  })

  const { field, sort } = useMemo(
    () => (config.sortModel?.length ? config.sortModel[0] : { field: '', sort: '' }),
    [config.sortModel],
  )

  const {
    isFetching,
    isLoading,
    refetch,
    data: kymPersons,
  } = useFindKymPersonsQuery({
    limit: config.rowsPerPage,
    page,
    offset: config.rowsPerPage * page,
    searchText: searchTextDebounced,
    nationality,
    sortField: field || 'createdAt',
    sortDirection: sort === 'desc' ? -1 : 1,
  })

  const apiRef = useGridApiRef()

  const handleChangePage = (_, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    saveConfig({ rowsPerPage: newRowsPerPage })
    setPage(0)
  }

  const handleColumnOrderChange = (params) => {
    const updatedOrder = updateListOrder(config.columnOrder, params)
    if (JSON.stringify(config.columnOrder) !== JSON.stringify(updatedOrder)) {
      saveConfig({ columnOrder: updatedOrder })
    }
  }

  const orderedColumns = useMemo(
    () => getOrderedColumns(config.columnOrder, columns),
    [JSON.stringify(config.columnOrder), columns],
  )

  const visibleColumnsModel = useMemo(
    () => getVisibilityModel(config.visibleColumns, columns),
    [config.visibleColumns, columns],
  )

  return (
    <Box>
      <Box className={`${styles.container} filter`}>
        <Typography variant="h2" className={styles.pageTitle}>
          KYM - {t('common:knowYourManager')}
        </Typography>
        <Box className={styles.actionsContainer} sx={{ m: 1, gap: 2 }}>
          <TextField
            sx={{ width: 350 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('common:searchByText')}
            variant="outlined"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton alt="Clear" disabled={!searchText} onClick={() => setSearchText('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            size="small"
            sx={{ width: 230 }}
            value={nationalityOptions.find((option) => option.value === nationality)}
            onChange={(_, newValue) => dispatch(setCurrentNationality(newValue?.value))}
            options={nationalityOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" placeholder={t('common:selectNationality')} />
            )}
            disablePortal
          />
        </Box>
      </Box>
      <Paper elevation={0}>
        <TableContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 16px',
            }}
          >
            <TablePagination
              labelRowsPerPage={[]}
              component="div"
              count={kymPersons?.pagination?.totalElements || 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={config.rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Button
                title={t('common:showHideColumns')}
                variant="text"
                aria-label={t('common:showHideColumns')}
                onClick={() => apiRef.current.showPreferences('columns')}
                sx={{
                  padding: 2,
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <Visibility fontSize="medium" sx={{ color: '#00C43D' }} />
              </Button>

              <Button onClick={refetch} disabled={isLoading} title={t('common:refresh')}>
                <RefreshIcon color="action" />
              </Button>
            </Box>
          </Box>

          <DataGridPro
            getRowId={(row) => row._id}
            rows={Array.isArray(kymPersons?.data) ? kymPersons.data : []}
            columns={orderedColumns}
            apiRef={apiRef}
            slots={{
              columnMenu: CustomColumnMenu,
              loadingOverlay: CustomLoadingOverlay,
            }}
            hideFooter={true}
            autoHeight={true}
            sortingMode="server"
            sortModel={config.sortModel}
            onSortModelChange={(newSortModel) => saveConfig({ sortModel: newSortModel })}
            loading={isFetching || isLoading || configLoading}
            columnOrder={config.columnOrder}
            onColumnOrderChange={handleColumnOrderChange}
            pinnedColumns={config.pinnedColumns}
            onPinnedColumnsChange={(newPinnedColumns) => saveConfig({ pinnedColumns: newPinnedColumns })}
            columnVisibilityModel={visibleColumnsModel}
            onColumnVisibilityModelChange={(newModel) => saveConfig({ visibleColumns: newModel })}
          />
        </TableContainer>
      </Paper>
    </Box>
  )
}

export default KymPersons
